import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Slider,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceArea,
} from 'recharts';
import { convertDistance, convertSpeed, convertElevation, getUnits } from '../pages/WorkoutAnalysisV2';

const CLIMB_CATEGORY_FILL = {
  'B-C4': '#ffe066',
  'B-C3': '#ffa020',
  'B-C2': '#e05000',
  'B-C1': '#c01010',
  'B-HC': '#7a0000',
};

const DESCENT_CATEGORY_FILL = {
  'B-D4':  '#a0d0ff',
  'B-D3':  '#4090e0',
  'B-D2':  '#1060b0',
  'B-D1':  '#003880',
  'B-DHC': '#001840',
};

function smoothData(data, window, xKey) {
  if (window <= 1) return data;
  return data.map((point, i) => {
    const half = Math.floor(window / 2);
    const start = Math.max(0, i - half);
    const end = Math.min(data.length - 1, i + half);
    const slice = data.slice(start, end + 1);
    const smoothed = { ...point };
    Object.keys(point).forEach(k => {
      if (k !== xKey && typeof point[k] === 'number') {
        smoothed[k] = slice.reduce((s, p) => s + (p[k] ?? 0), 0) / slice.length;
      }
    });
    return smoothed;
  });
}

function lerp(start, end, t) { return start + (end - start) * t; }

const WorkoutChartsSection = ({
  chunks,
  climbs = [],
  descents = [],
  activeClimbRange = null,
  activeDescentRange = null,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [units, setUnits] = useState(getUnits());
  React.useEffect(() => {
    const handler = (e) => setUnits(e.detail || getUnits());
    window.addEventListener('unitsChanged', handler);
    return () => window.removeEventListener('unitsChanged', handler);
  }, []);

  const speedLabel = units === 'imperial' ? 'mph' : 'km/h';
  const elevLabel  = units === 'imperial' ? 'ft' : 'm';
  const toDisplayKm = (km) => units === 'imperial' ? km * 0.621371 : km;

  // ─── Distance-Based Chart state ────────────────────────────────────────
  const [distMetrics, setDistMetrics] = useState(['speed']);
  const [distSmoothing, setDistSmoothing] = useState(1);
  const [distZoomLeft,  setDistZoomLeft]  = useState('dataMin');
  const [distZoomRight, setDistZoomRight] = useState('dataMax');
  const [distRefLeft,   setDistRefLeft]   = useState(null);
  const [distRefRight,  setDistRefRight]  = useState(null);
  const [distSelecting, setDistSelecting] = useState(false);

  const resetDistZoom = () => { setDistZoomLeft('dataMin'); setDistZoomRight('dataMax'); };
  const onDistMouseDown = (e) => { if (e?.activeLabel != null) { setDistRefLeft(e.activeLabel); setDistRefRight(e.activeLabel); setDistSelecting(true); } };
  const onDistMouseMove = (e) => { if (distSelecting && e?.activeLabel != null) setDistRefRight(e.activeLabel); };
  const onDistMouseUp   = () => {
    if (distSelecting && distRefLeft != null && distRefRight != null && distRefLeft !== distRefRight) {
      setDistZoomLeft(Math.min(distRefLeft, distRefRight));
      setDistZoomRight(Math.max(distRefLeft, distRefRight));
    }
    setDistRefLeft(null); setDistRefRight(null); setDistSelecting(false);
  };

  // ─── Time-Based Chart state ─────────────────────────────────────────────
  const [timeMetrics, setTimeMetrics] = useState(['speed']);
  const [timeSmoothing, setTimeSmoothing] = useState(1);
  const [timeZoomLeft,  setTimeZoomLeft]  = useState('dataMin');
  const [timeZoomRight, setTimeZoomRight] = useState('dataMax');
  const [timeRefLeft,   setTimeRefLeft]   = useState(null);
  const [timeRefRight,  setTimeRefRight]  = useState(null);
  const [timeSelecting, setTimeSelecting] = useState(false);

  const resetTimeZoom = () => { setTimeZoomLeft('dataMin'); setTimeZoomRight('dataMax'); };
  const onTimeMouseDown = (e) => { if (e?.activeLabel != null) { setTimeRefLeft(e.activeLabel); setTimeRefRight(e.activeLabel); setTimeSelecting(true); } };
  const onTimeMouseMove = (e) => { if (timeSelecting && e?.activeLabel != null) setTimeRefRight(e.activeLabel); };
  const onTimeMouseUp   = () => {
    if (timeSelecting && timeRefLeft != null && timeRefRight != null && timeRefLeft !== timeRefRight) {
      setTimeZoomLeft(Math.min(timeRefLeft, timeRefRight));
      setTimeZoomRight(Math.max(timeRefLeft, timeRefRight));
    }
    setTimeRefLeft(null); setTimeRefRight(null); setTimeSelecting(false);
  };

  // ─── Data computation ────────────────────────────────────────────────────
  const rawDistData = useMemo(() => {
    if (!chunks?.length) return [];
    let cumDist = 0;
    let cumPowerNumer = 0;
    let cumPowerDenom = 0;
    return chunks.map((c) => {
      cumDist += c.distance || 0;
      if (c.avg_power != null && (c.duration || 0) > 0) {
        cumPowerNumer += c.avg_power * c.duration;
        cumPowerDenom += c.duration;
      }
      const positionValue = c.what_if?.actual_position === 'tt_aero'   ? 1 :
                            c.what_if?.actual_position === 'tt_base'   ? 2 :
                            c.what_if?.actual_position === 'road_drops'? 3 :
                            c.what_if?.actual_position === 'road_hoods'? 4 : null;
      return {
        distance:        parseFloat(toDisplayKm(cumDist / 1000).toFixed(2)),
        altitude:        parseFloat(convertElevation(c.avg_altitude, units).toFixed(1)),
        speed:           parseFloat(convertSpeed(c.avg_speed, units).toFixed(1)),
        gradient:        parseFloat((c.avg_gradient || 0).toFixed(2)),
        power:           c.avg_power != null ? parseFloat(c.avg_power.toFixed(0)) : null,
        avg_power_cum:   cumPowerDenom > 0 ? parseFloat((cumPowerNumer / cumPowerDenom).toFixed(0)) : null,
        assumed_cda:     c.assumed_cda    ? parseFloat(c.assumed_cda.toFixed(3))    : null,
        calculated_cda:  c.calculated_cda ? parseFloat(c.calculated_cda.toFixed(3)) : null,
        position:        positionValue,
        curvature_index: typeof c.curvature_index === 'number' ? c.curvature_index : null,
        stamina_score:   c.stamina_score != null ? parseFloat(c.stamina_score.toFixed(1)) : null,
        positionLabel:   c.what_if?.actual_position || null,
      };
    });
  }, [chunks, units]); // eslint-disable-line react-hooks/exhaustive-deps

  const distData = useMemo(() => smoothData(rawDistData, distSmoothing, 'distance'), [rawDistData, distSmoothing]);

  const rawTimeData = useMemo(() => {
    if (!chunks?.length) return [];
    const TIME_INTERVAL = 5;
    let cumTime = 0;
    const ranges = chunks.map((c, idx) => {
      const start = cumTime;
      cumTime += c.duration;
      return { c, start, end: cumTime, idx };
    });
    const total = cumTime;
    const result = [];
    for (let sec = 0; sec <= total; sec += TIME_INTERVAL) {
      const cr = ranges.find(r => sec >= r.start && sec <= r.end);
      if (!cr) continue;
      const { c, start: rStart, idx } = cr;
      const next = ranges[idx + 1];
      const progress = (sec - rStart) / (c.duration || 1);
      let alt, spd, grad, pwr, acda, ccda, stamina, posLabel;
      if (next && progress > 0.5) {
        const blend = (progress - 0.5) * 2;
        alt     = lerp(c.avg_altitude, next.c.avg_altitude, blend);
        spd     = lerp(c.avg_speed,    next.c.avg_speed,    blend);
        grad    = lerp(c.avg_gradient, next.c.avg_gradient, blend);
        pwr     = lerp(c.avg_power,    next.c.avg_power,    blend);
        acda    = c.assumed_cda    && next.c.assumed_cda    ? lerp(c.assumed_cda,    next.c.assumed_cda,    blend) : c.assumed_cda;
        ccda    = c.calculated_cda && next.c.calculated_cda ? lerp(c.calculated_cda, next.c.calculated_cda, blend) : c.calculated_cda;
        stamina = c.stamina_score != null && next.c.stamina_score != null ? lerp(c.stamina_score, next.c.stamina_score, blend) : c.stamina_score;
        posLabel = blend > 0.5 ? (next.c.what_if?.actual_position || null) : (c.what_if?.actual_position || null);
      } else {
        alt = c.avg_altitude; spd = c.avg_speed; grad = c.avg_gradient; pwr = c.avg_power;
        acda = c.assumed_cda; ccda = c.calculated_cda; stamina = c.stamina_score;
        posLabel = c.what_if?.actual_position || null;
      }
      const posVal = posLabel === 'tt_aero' ? 1 : posLabel === 'tt_base' ? 2 : posLabel === 'road_drops' ? 3 : posLabel === 'road_hoods' ? 4 : null;
      result.push({
        time:           parseFloat((sec / 60).toFixed(2)),
        altitude:       parseFloat(convertElevation(alt, units).toFixed(1)),
        speed:          parseFloat(convertSpeed(spd, units).toFixed(1)),
        gradient:       parseFloat((grad || 0).toFixed(2)),
        power:          pwr != null ? parseFloat(pwr.toFixed(0)) : null,
        assumed_cda:    acda    ? parseFloat(acda.toFixed(3))    : null,
        calculated_cda: ccda    ? parseFloat(ccda.toFixed(3))    : null,
        position:       posVal,
        curvature_index: typeof c.curvature_index === 'number' ? c.curvature_index : null,
        stamina_score:  stamina != null ? parseFloat(stamina.toFixed(1)) : null,
        positionLabel:  posLabel,
      });
    }
    return result;
  }, [chunks, units]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeData = useMemo(() => smoothData(rawTimeData, timeSmoothing, 'time'), [rawTimeData, timeSmoothing]);

  if (!chunks?.length) return null;

  const hasPower = chunks.some(c => c.avg_power != null && c.avg_power > 0);
  const hasAssumedCda  = chunks.some(c => c.assumed_cda != null);
  const hasCalcCda     = chunks.some(c => c.calculated_cda != null);
  const hasPosition    = chunks.some(c => c.what_if?.actual_position != null);
  const hasCurvature   = chunks.some(c => c.curvature_index != null);
  const hasStamina     = chunks.some(c => c.stamina_score != null);

  const metricOptions = [
    { key: 'speed',          label: t('speedLineName', 'Speed') },
    { key: 'gradient',       label: t('gradientLineName', 'Gradient') },
    ...(hasPower         ? [{ key: 'power',          label: t('powerLineName', 'Power') }]          : []),
    ...(hasPower         ? [{ key: 'avg_power_cum',  label: t('avgPowerCumLineName', 'Avg Power (cumulative)') }] : []),
    ...(hasAssumedCda    ? [{ key: 'assumed_cda',    label: t('assumedCdaLineName', 'Assumed CdA') }]    : []),
    ...(hasCalcCda       ? [{ key: 'calculated_cda', label: t('calculatedCdaLineName', 'Calculated CdA') }] : []),
    ...(hasPosition      ? [{ key: 'position',       label: t('positionLineName', 'Position') }]    : []),
    ...(hasCurvature     ? [{ key: 'curvature_index',label: t('twistiness', 'Twistiness Index') }]  : []),
    ...(hasStamina       ? [{ key: 'stamina_score',  label: t('staminaScore', 'Stamina Score') }]   : []),
  ];

  const metricColors = {
    speed:          colors.chartColors[2],
    gradient:       colors.chartColors[3],
    power:          colors.chartColors[4],
    avg_power_cum:  colors.chartColors[12],
    assumed_cda:    colors.chartColors[5],
    calculated_cda: colors.chartColors[6],
    position:       colors.chartColors[7],
    curvature_index:colors.chartColors[8],
    stamina_score:  colors.greenAccent[500],
  };

  const formatMetric = (key, value, payload) => {
    if (value == null) return 'N/A';
    switch (key) {
      case 'altitude':       return `${value.toFixed(0)} ${elevLabel}`;
      case 'speed':          return `${value.toFixed(1)} ${speedLabel}`;
      case 'power':
      case 'avg_power_cum':  return `${Math.round(value)} W`;
      case 'gradient':       return `${value.toFixed(1)}%`;
      case 'position':       return payload?.positionLabel || value;
      case 'assumed_cda':
      case 'calculated_cda': return value.toFixed(3);
      default:               return value.toFixed(2);
    }
  };

  const makeTooltip = (xKey, xLabel, xFormatter) => {
    return ({ active, payload }) => {
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload;
      return (
        <Box sx={{ backgroundColor: colors.grey[800], border: `1px solid ${colors.grey[700]}`, borderRadius: '4px', p: 1.5 }}>
          <Typography variant="body2" fontWeight="bold" color={colors.grey[100]} mb={0.5}>
            {`${xLabel}: ${xFormatter(row?.[xKey])}`}
          </Typography>
          {payload.map((entry, i) => (
            <Typography key={i} variant="body2" color={entry.color}>
              {entry.name}: {formatMetric(entry.dataKey, entry.value, row)}
            </Typography>
          ))}
        </Box>
      );
    };
  };

  const DistTooltip = makeTooltip('distance', t('distance', 'Distance'), (v) => `${v?.toFixed(1)} ${units === 'imperial' ? 'mi' : 'km'}`);
  const TimeTooltip = makeTooltip('time', t('timeAxisLabel', 'Time'), (v) => `${v?.toFixed(1)} min`);

  const SmootingControl = ({ value, onChange }) => (
    <Box sx={{ position: 'relative', border: `1px solid ${colors.grey[600]}`, borderRadius: '4px', px: 2, pt: 1, pb: 1, minWidth: 200 }}>
      <Typography variant="caption" sx={{ position: 'absolute', top: -10, left: 10, px: 0.5, backgroundColor: colors.grey[800], color: colors.grey[300], fontSize: '0.75rem' }}>
        {value === 1 ? t('smoothingOff', 'Smoothing (off)') : t('smoothingChunks', 'Smoothing ({{count}} chunks)', { count: value })}
      </Typography>
      <Slider value={value} min={1} max={20} step={1} onChange={(_, v) => onChange(v)} sx={{ color: colors.blueAccent[400], display: 'block', mt: 0.5 }} size="small" />
    </Box>
  );

  const MetricSelect = ({ value, onChange }) => (
    <FormControl sx={{ minWidth: isMobile ? '100%' : 240, width: isMobile ? '100%' : 'auto' }}>
      <InputLabel sx={{ color: colors.grey[300] }}>{t('selectMetrics', 'Select Metrics')}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
        input={<OutlinedInput label={t('selectMetrics', 'Select Metrics')} />}
        renderValue={(sel) => sel.map(k => metricOptions.find(o => o.key === k)?.label || k).join(', ')}
        sx={{ color: colors.grey[100], '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] } }}
      >
        {metricOptions.map(o => (
          <MenuItem key={o.key} value={o.key}>
            <Checkbox checked={value.includes(o.key)} />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const commonYAxes = (isMobile, colors, t) => ([
    <YAxis
      key="left"
      yAxisId="left"
      stroke={colors.grey[100]}
      width={isMobile ? 0 : 55}
      tick={!isMobile}
      axisLine={!isMobile}
      tickLine={!isMobile}
      label={isMobile ? undefined : { value: t('altitude', 'Altitude'), angle: -90, position: 'insideLeft', fill: colors.grey[300] }}
    />,
    <YAxis
      key="right"
      yAxisId="right"
      orientation="right"
      stroke={colors.grey[100]}
      width={isMobile ? 0 : 55}
      tick={!isMobile}
      axisLine={!isMobile}
      tickLine={!isMobile}
      label={isMobile ? undefined : { value: t('metricsYAxis', 'Metrics'), angle: 90, position: 'insideRight', fill: colors.grey[300] }}
    />,
  ]);

  const metricLines = (selected) => selected.map(key => (
    <Line
      key={key}
      yAxisId="right"
      type="monotone"
      dataKey={key}
      stroke={metricColors[key]}
      name={metricOptions.find(o => o.key === key)?.label || key}
      dot={false}
      strokeWidth={2}
      strokeDasharray={key === 'calculated_cda' ? '5 5' : undefined}
      connectNulls={false}
    />
  ));

  const climbBands = (yId = 'left') => [
    ...climbs.map((c, i) => (
      <ReferenceArea key={`c-${i}`} yAxisId={yId}
        x1={toDisplayKm(c.start_distance_m / 1000)} x2={toDisplayKm(c.end_distance_m / 1000)}
        fill={CLIMB_CATEGORY_FILL[c.category] || CLIMB_CATEGORY_FILL['B-C4']}
        fillOpacity={0.22} stroke={CLIMB_CATEGORY_FILL[c.category] || CLIMB_CATEGORY_FILL['B-C4']} strokeOpacity={0.5} strokeWidth={1} ifOverflow="hidden"
      />
    )),
    ...descents.map((d, i) => (
      <ReferenceArea key={`d-${i}`} yAxisId={yId}
        x1={toDisplayKm(d.start_distance_m / 1000)} x2={toDisplayKm(d.end_distance_m / 1000)}
        fill={DESCENT_CATEGORY_FILL[d.category] || DESCENT_CATEGORY_FILL['B-D4']}
        fillOpacity={0.22} stroke={DESCENT_CATEGORY_FILL[d.category] || DESCENT_CATEGORY_FILL['B-D4']} strokeOpacity={0.5} strokeWidth={1} ifOverflow="hidden"
      />
    )),
    activeClimbRange  && <ReferenceArea key="ac" yAxisId={yId} x1={toDisplayKm(activeClimbRange.leftKm)}  x2={toDisplayKm(activeClimbRange.rightKm)}  fill={activeClimbRange.color  || '#e05000'} fillOpacity={0.35} stroke={activeClimbRange.color  || '#e05000'} strokeOpacity={0.9} strokeWidth={2} ifOverflow="hidden" />,
    activeDescentRange && <ReferenceArea key="ad" yAxisId={yId} x1={toDisplayKm(activeDescentRange.leftKm)} x2={toDisplayKm(activeDescentRange.rightKm)} fill={activeDescentRange.color || '#4090e0'} fillOpacity={0.35} stroke={activeDescentRange.color || '#4090e0'} strokeOpacity={0.9} strokeWidth={2} ifOverflow="hidden" />,
  ];

  const ChartHeader = ({ title, zoomed, onResetZoom, smoothing, onSmoothing, metrics: sel, onMetrics }) => (
    <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'flex-start' : 'center'} mb={2} gap={isMobile ? 2 : 0}>
      <Box display="flex" alignItems="center" gap={2}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" color={colors.grey[100]}>
          {title}
        </Typography>
        {zoomed && (
          <button
            onClick={onResetZoom}
            style={{ background: colors.grey[700], color: colors.grey[100], border: `1px solid ${colors.grey[500]}`, borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: '0.75rem' }}
          >
            {t('resetZoom', 'Reset Zoom')}
          </button>
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={3} flexDirection={isMobile ? 'column' : 'row'} width={isMobile ? '100%' : 'auto'}>
        <SmootingControl value={smoothing} onChange={onSmoothing} />
        <MetricSelect value={sel} onChange={onMetrics} />
      </Box>
    </Box>
  );

  const distZoomed = distZoomLeft !== 'dataMin' || distZoomRight !== 'dataMax';
  const timeZoomed = timeZoomLeft !== 'dataMin' || timeZoomRight !== 'dataMax';

  return (
    <>
      {/* ── Distance-Based Chart ── */}
      <Box mt={4}>
        <Paper elevation={3} sx={{ p: isMobile ? 1 : 3, backgroundColor: colors.grey[800], borderRadius: '8px' }}>
          <ChartHeader
            title={t('distanceBasedAnalysis', 'Distance-Based Analysis')}
            zoomed={distZoomed}
            onResetZoom={resetDistZoom}
            smoothing={distSmoothing}
            onSmoothing={setDistSmoothing}
            metrics={distMetrics}
            onMetrics={setDistMetrics}
          />
          <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
            {t('dragToZoom', 'Drag on the chart to zoom in')}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={distData}
              margin={isMobile ? { top: 5, right: 5, left: 0, bottom: 5 } : { top: 5, right: 60, left: 10, bottom: 5 }}
              onMouseDown={onDistMouseDown}
              onMouseMove={onDistMouseMove}
              onMouseUp={onDistMouseUp}
              onMouseLeave={onDistMouseUp}
              style={{ userSelect: 'none' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
              <XAxis
                dataKey="distance"
                type="number"
                domain={[distZoomLeft, distZoomRight]}
                allowDataOverflow
                stroke={colors.grey[100]}
                tickFormatter={(v) => v.toFixed(1)}
                tick={isMobile ? { fill: colors.grey[400], fontSize: 10 } : { fill: colors.grey[400], fontSize: 11 }}
              />
              {commonYAxes(isMobile, colors, t)}
              <Tooltip content={<DistTooltip />} />
              <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="bottom" />

              <Line yAxisId="left" type="monotone" dataKey="altitude"
                stroke={colors.chartColors[1]} name={t('altitudeLineName', 'Altitude')} dot={false} strokeWidth={2} />

              {metricLines(distMetrics)}
              {climbBands('left')}

              {distRefLeft != null && distRefRight != null && (
                <ReferenceArea yAxisId="left" x1={Math.min(distRefLeft, distRefRight)} x2={Math.max(distRefLeft, distRefRight)} fill={colors.blueAccent[700]} fillOpacity={0.3} strokeOpacity={0.3} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* ── What-If Altitude Profile ── */}
      <Box mt={4}>
        <Paper elevation={3} sx={{ p: isMobile ? 1 : 3, backgroundColor: colors.grey[800], borderRadius: '8px' }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" color={colors.grey[100]} mb={1}>
            {t('whatIfAltitudeProfile', 'What-If Analysis: Altitude Profile')}
          </Typography>
          <Typography variant="body2" color={colors.grey[300]} mb={2}>
            {t('altitudeProfileLegend', 'Green: Speed gain in what-if scenario | Red: Speed loss in what-if scenario')}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={(() => {
                let cumDist = 0;
                const pts = chunks.map((c) => {
                  cumDist += c.distance;
                  const diff = c.what_if ? (c.what_if.speed_with_both_changes - c.avg_speed) : 0;
                  return { distance: parseFloat(convertDistance(cumDist, units).toFixed(2)), altitude: parseFloat(c.avg_altitude.toFixed(1)), diff, isGain: diff > 0.1, isZero: Math.abs(diff) <= 0.1 };
                });
                const result = [];
                pts.forEach((p, i) => {
                  result.push({ distance: p.distance, altitude: p.altitude, altitudeGain: p.isGain ? p.altitude : null, altitudeZero: p.isZero ? p.altitude : null, altitudeLoss: !p.isGain && !p.isZero ? p.altitude : null, speedDiff: p.diff });
                  if (i < pts.length - 1) {
                    const cur = p.isZero ? 'z' : p.isGain ? 'g' : 'l';
                    const nxt = pts[i+1].isZero ? 'z' : pts[i+1].isGain ? 'g' : 'l';
                    if (cur !== nxt) result.push({ distance: p.distance, altitude: p.altitude, altitudeGain: p.altitude, altitudeZero: p.altitude, altitudeLoss: p.altitude, speedDiff: p.diff });
                  }
                });
                return result;
              })()}
              margin={isMobile ? { top: 5, right: 5, left: 0, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
              <XAxis dataKey="distance" stroke={colors.grey[100]} tickFormatter={(v) => `${v.toFixed(units === 'imperial' ? 2 : 1)}`} />
              <YAxis
                stroke={colors.grey[100]}
                width={isMobile ? 0 : 60}
                tick={!isMobile}
                axisLine={!isMobile}
                tickLine={!isMobile}
                label={isMobile ? undefined : { value: t('altitudeAxisLabel', 'Altitude (m)'), angle: -90, position: 'insideLeft', fill: colors.grey[300] }}
              />
              <Tooltip
                contentStyle={{ backgroundColor: colors.grey[900], border: `1px solid ${colors.grey[700]}`, borderRadius: '4px' }}
                labelStyle={{ color: colors.grey[100] }}
                formatter={(value, name) => {
                  if (name === t('speedGainLegend', 'Speed Gain') || name === t('speedLossLegend', 'Speed Loss') || name === t('speedNoChangeLegend', 'No Change')) {
                    return [value ? `${value} m` : 'N/A', t('altitudeTooltip', 'Altitude')];
                  }
                  return [value, name];
                }}
              />
              <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="bottom" />
              <Line type="monotone" dataKey="altitudeGain" stroke={colors.greenAccent[500]} dot={false} strokeWidth={3} name={t('speedGainLegend', 'Speed Gain')} connectNulls={false} />
              <Line type="monotone" dataKey="altitudeZero" stroke={colors.blueAccent[500]}  dot={false} strokeWidth={3} name={t('speedNoChangeLegend', 'No Change')} connectNulls={false} />
              <Line type="monotone" dataKey="altitudeLoss" stroke={colors.redAccent[500]}   dot={false} strokeWidth={3} name={t('speedLossLegend', 'Speed Loss')} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* ── Time-Based Chart ── */}
      <Box mt={4}>
        <Paper elevation={3} sx={{ p: isMobile ? 1 : 3, backgroundColor: colors.grey[800], borderRadius: '8px' }}>
          <ChartHeader
            title={t('timeBasedAnalysis', 'Time-Based Analysis')}
            zoomed={timeZoomed}
            onResetZoom={resetTimeZoom}
            smoothing={timeSmoothing}
            onSmoothing={setTimeSmoothing}
            metrics={timeMetrics}
            onMetrics={setTimeMetrics}
          />
          <Typography variant="caption" color={colors.grey[400]} mb={1} display="block">
            {t('dragToZoom', 'Drag on the chart to zoom in')}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={timeData}
              margin={isMobile ? { top: 5, right: 5, left: 0, bottom: 5 } : { top: 5, right: 60, left: 10, bottom: 5 }}
              onMouseDown={onTimeMouseDown}
              onMouseMove={onTimeMouseMove}
              onMouseUp={onTimeMouseUp}
              onMouseLeave={onTimeMouseUp}
              style={{ userSelect: 'none' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
              <XAxis
                dataKey="time"
                type="number"
                domain={[timeZoomLeft, timeZoomRight]}
                allowDataOverflow
                stroke={colors.grey[100]}
                tickFormatter={(v) => v.toFixed(0)}
                tick={isMobile ? { fill: colors.grey[400], fontSize: 10 } : { fill: colors.grey[400], fontSize: 11 }}
              />
              {commonYAxes(isMobile, colors, t)}
              <Tooltip content={<TimeTooltip />} />
              <Legend wrapperStyle={{ color: colors.grey[100] }} verticalAlign="bottom" />

              <Line yAxisId="left" type="monotone" dataKey="altitude"
                stroke={colors.chartColors[1]} name={t('altitudeLineName', 'Altitude')} dot={false} strokeWidth={2} />

              {metricLines(timeMetrics)}

              {timeRefLeft != null && timeRefRight != null && (
                <ReferenceArea yAxisId="left" x1={Math.min(timeRefLeft, timeRefRight)} x2={Math.max(timeRefLeft, timeRefRight)} fill={colors.blueAccent[700]} fillOpacity={0.3} strokeOpacity={0.3} />
              )}
            </LineChart>
          </ResponsiveContainer>

          {timeMetrics.includes('position') && (
            <Box mt={2} display="flex" justifyContent="center" gap={3} flexWrap="wrap">
              {[['1: TT Aero', 'positionLegend1'], ['2: TT Base', 'positionLegend2'], ['3: Road Drops', 'positionLegend3'], ['4: Road Hoods', 'positionLegend4']].map(([fallback, key]) => (
                <Box key={key} display="flex" alignItems="center" gap={1}>
                  <Box width={12} height={12} bgcolor={metricColors.position} />
                  <Typography variant="caption" color={colors.grey[300]}>{t(key, fallback)}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default WorkoutChartsSection;
